import { useState } from 'react';

import { Button, CircularProgress, Link, Typography } from '@mui/material';
import { generatePath, Link as RouterLink } from 'react-router-dom';

import { useConfirmOrder } from 'entities/chat/api/useConfirmOrder';
import type { OrderPreview } from 'features/onboarding/lib/conversation';
import { TruvAvatar } from 'features/onboarding/ui/OnboardingMessages';
import { urls } from 'shared/urls';

type State =
  | { kind: 'idle' }
  | { kind: 'placed'; orderGroupId: string }
  | { kind: 'cancelled' }
  | { kind: 'error'; message: string };

function draftText(draft: Record<string, unknown>, key: string): string {
  const value = draft[key];
  return typeof value === 'string' ? value : '';
}

function draftProducts(draft: Record<string, unknown>): string[] {
  return Array.isArray(draft.products) ? draft.products.filter((item): item is string => typeof item === 'string') : [];
}

// The confirm step for an order the assistant previewed. Placing is the only write path; it calls
// POST /v1/chat/confirm/ with the preview token + a stable idempotency key.
export function OrderPreviewCard({ preview, idempotencyKey }: { preview: OrderPreview; idempotencyKey: string }) {
  const [state, setState] = useState<State>({ kind: 'idle' });
  const { mutateAsync: confirm, isPending: placing } = useConfirmOrder();
  const applicant = `${draftText(preview.draft, 'first_name')} ${draftText(preview.draft, 'last_name')}`.trim();
  const contact = draftText(preview.draft, 'email') || draftText(preview.draft, 'phone');
  const products = draftProducts(preview.draft);
  const summary = [applicant, contact].filter(Boolean).join(' · ');

  const place = async () => {
    if (placing) {
      return;
    }
    const result = await confirm({ previewToken: preview.previewToken, idempotencyKey, threadId: preview.threadId });
    setState(
      result.ok ? { kind: 'placed', orderGroupId: result.orderGroupId } : { kind: 'error', message: result.message },
    );
  };

  const footer = (() => {
    if (state.kind === 'placed') {
      return (
        <Typography className="mt-3 text-sm text-success">
          Order placed —{' '}
          <Link component={RouterLink} to={generatePath(urls.orderInformation.url(), { orderId: state.orderGroupId })}>
            view order
          </Link>
        </Typography>
      );
    }
    if (state.kind === 'cancelled') {
      return (
        <Typography variant="body2" className="mt-3 text-grey60">
          Cancelled.
        </Typography>
      );
    }
    return (
      <div className="mt-3 flex items-center gap-2">
        <Button variant="contained" size="small" disabled={placing} onClick={place} sx={{ textTransform: 'none' }}>
          {placing ? <CircularProgress size={14} color="inherit" /> : 'Place order'}
        </Button>
        <Button
          variant="text"
          size="small"
          disabled={placing}
          onClick={() => setState({ kind: 'cancelled' })}
          sx={{ textTransform: 'none', color: 'text.secondary' }}
        >
          Cancel
        </Button>
        {state.kind === 'error' && (
          <Typography variant="body2" color="error">
            {state.message}
          </Typography>
        )}
      </div>
    );
  })();

  return (
    <div className="flex items-start gap-2">
      <TruvAvatar />
      <div className="min-w-0 flex-1 rounded-2xl border border-grey30 bg-white p-4">
        <Typography className="font-semibold text-grey90">Confirm order</Typography>
        {summary && (
          <Typography variant="body2" className="mt-0.5 text-grey70">
            {summary}
          </Typography>
        )}
        {products.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {products.map((product) => (
              <span key={product} className="rounded-full bg-grey20 px-2 py-0.5 text-grey80 text-xs">
                {product}
              </span>
            ))}
          </div>
        )}
        {footer}
      </div>
    </div>
  );
}
